import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, clearToken, getToken } from "@/lib/api";
import { LogOut, Inbox, CalendarDays, Trash2, RefreshCw } from "lucide-react";

const STATUS_LABELS = {
  uj: "Új",
  folyamatban: "Folyamatban",
  kesz: "Kész",
  torolve: "Törölve",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const logout = () => {
    clearToken();
    nav("/admin/login", { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [bRes, mRes] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/messages"),
      ]);
      setBookings(bRes.data);
      setMessages(mRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        nav("/admin/login", { replace: true });
        return;
      }
      setError("Hiba a betöltés közben.");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Státusz frissítés sikertelen:", err);
      }
      setError("Nem sikerült a státusz frissítése.");
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Biztosan törli a foglalást?")) return;
    await api.delete(`/admin/bookings/${id}`);
    setBookings((prev) => prev.filter((b) => b.id !== id));
  };

  const removeMessage = async (id) => {
    if (!window.confirm("Biztosan törli az üzenetet?")) return;
    await api.delete(`/admin/messages/${id}`);
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="admin-dashboard-page"
    >
      <header className="border-b border-white/5 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="m-stripe h-[3px] w-full" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-4"
            data-testid="admin-logo-link"
          >
            <div className="m-stripe w-1.5 h-12" />
            <div>
              <div
                className="text-3xl font-black uppercase tracking-tight"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Autó-<span className="text-[#0082D2]">Szerelem</span>
              </div>
              <div
                className="text-[0.7rem] uppercase tracking-[0.24em] text-zinc-500 mt-1"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Admin Panel
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="text-xs uppercase tracking-[0.18em] text-zinc-400 hover:text-white px-4 py-2 border border-white/10 hover:border-white/30 transition-colors flex items-center gap-2"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Frissítés
            </button>
            <button
              onClick={logout}
              className="text-xs uppercase tracking-[0.18em] text-zinc-400 hover:text-red-400 px-4 py-2 border border-white/10 hover:border-red-500/40 transition-colors flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Kilépés
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid sm:grid-cols-2 gap-5 mb-10">
          <Stat
            label="Foglalások"
            value={bookings.length}
            icon={CalendarDays}
            testId="stat-bookings"
          />
          <Stat
            label="Üzenetek"
            value={messages.length}
            icon={Inbox}
            testId="stat-messages"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-8 border-b border-white/10 mb-8">
          <TabBtn
            active={tab === "bookings"}
            onClick={() => setTab("bookings")}
            testId="admin-tab-bookings"
          >
            Foglalások ({bookings.length})
          </TabBtn>
          <TabBtn
            active={tab === "messages"}
            onClick={() => setTab("messages")}
            testId="admin-tab-messages"
          >
            Üzenetek ({messages.length})
          </TabBtn>
        </div>

        {error && (
          <div className="mb-6 text-sm text-red-400 border border-red-500/30 bg-red-500/5 px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500 py-20 text-center" data-testid="loading-indicator">
            Betöltés...
          </div>
        ) : tab === "bookings" ? (
          <BookingsTable
            items={bookings}
            onStatus={updateStatus}
            onDelete={removeBooking}
          />
        ) : (
          <MessagesTable items={messages} onDelete={removeMessage} />
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div
      className="bg-[#101010] border border-white/5 p-6 flex items-center justify-between"
      data-testid={testId}
    >
      <div>
        <div
          className="text-[0.7rem] uppercase tracking-[0.24em] text-zinc-500"
          style={{ fontFamily: "Barlow Condensed" }}
        >
          {label}
        </div>
        <div
          className="text-5xl font-black text-white mt-1"
          style={{ fontFamily: "Barlow Condensed" }}
        >
          {value}
        </div>
      </div>
      <div className="w-12 h-12 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/30">
        <Icon className="w-6 h-6 text-[#0082D2]" strokeWidth={1.5} />
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`pb-3 text-sm font-bold uppercase tracking-[0.2em] transition-colors border-b-2 -mb-px ${
        active
          ? "text-white border-[#0082D2]"
          : "text-zinc-500 border-transparent hover:text-zinc-300"
      }`}
      style={{ fontFamily: "Barlow Condensed" }}
    >
      {children}
    </button>
  );
}

function BookingsTable({ items, onStatus, onDelete }) {
  if (items.length === 0)
    return (
      <div className="text-center py-20 text-zinc-500 border border-dashed border-white/10">
        Még nincsenek foglalások.
      </div>
    );

  return (
    <div
      className="overflow-x-auto border border-white/5"
      data-testid="bookings-table"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#101010] text-left text-[0.65rem] uppercase tracking-[0.18em] text-zinc-500">
            <th className="px-4 py-3 font-semibold">Dátum</th>
            <th className="px-4 py-3 font-semibold">Név</th>
            <th className="px-4 py-3 font-semibold">Kapcsolat</th>
            <th className="px-4 py-3 font-semibold">Autó</th>
            <th className="px-4 py-3 font-semibold">Szolgáltatás</th>
            <th className="px-4 py-3 font-semibold">Időpont</th>
            <th className="px-4 py-3 font-semibold">Státusz</th>
            <th className="px-4 py-3 font-semibold text-right">Művelet</th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr
              key={b.id}
              className="border-t border-white/5 hover:bg-white/[0.02]"
              data-testid={`booking-row-${b.id}`}
            >
              <td className="px-4 py-4 text-zinc-400 mono text-xs whitespace-nowrap">
                {fmtDate(b.created_at)}
              </td>
              <td className="px-4 py-4 font-medium text-white">{b.name}</td>
              <td className="px-4 py-4 text-zinc-400">
                <div>{b.email}</div>
                <div className="text-xs">{b.phone}</div>
              </td>
              <td className="px-4 py-4 text-zinc-300">{b.car_model}</td>
              <td className="px-4 py-4 text-zinc-300 max-w-[200px]">
                {b.service}
              </td>
              <td className="px-4 py-4 text-zinc-400 mono text-xs whitespace-nowrap">
                {b.date_time}
              </td>
              <td className="px-4 py-4">
                <select
                  className={`pill pill-${b.status} bg-transparent cursor-pointer outline-none`}
                  value={b.status}
                  onChange={(e) => onStatus(b.id, e.target.value)}
                  data-testid={`booking-status-${b.id}`}
                >
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <option key={v} value={v} className="bg-black">
                      {l}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-4 py-4 text-right">
                <button
                  onClick={() => onDelete(b.id)}
                  className="text-zinc-500 hover:text-red-400 transition-colors"
                  data-testid={`booking-delete-${b.id}`}
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MessagesTable({ items, onDelete }) {
  if (items.length === 0)
    return (
      <div className="text-center py-20 text-zinc-500 border border-dashed border-white/10">
        Még nincsenek üzenetek.
      </div>
    );

  return (
    <div className="space-y-4" data-testid="messages-list">
      {items.map((m) => (
        <article
          key={m.id}
          className="bg-[#101010] border border-white/5 p-6"
          data-testid={`message-row-${m.id}`}
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <div className="text-white font-semibold">{m.name}</div>
              <a
                href={`mailto:${m.email}`}
                className="text-xs text-[#0082D2] hover:underline"
              >
                {m.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-zinc-500 mono whitespace-nowrap">
                {fmtDate(m.created_at)}
              </span>
              <button
                onClick={() => onDelete(m.id)}
                className="text-zinc-500 hover:text-red-400 transition-colors"
                data-testid={`message-delete-${m.id}`}
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
            {m.message}
          </p>
        </article>
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
